/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.heading',

	defaultMessage: 'Plans',
	description: 'Heading for Plans settings items',
});

const permissionsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.permissions-item',

	defaultMessage: 'Permissions',
	description: 'Navigation item which links to an Permissions settings page',
});

const hierarchyItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.hierarchy-item',

	defaultMessage: 'Hierarchy configuration',
	description: 'Navigation item which links to an Hierarchy configuration settings page',
});

const dependenciesItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.dependencies-item',

	defaultMessage: 'Dependencies',
	description: 'Navigation item which links to an Dependencies settings page',
});

const loadingLimitsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.loading-limits-item',

	defaultMessage: 'Loading limits',
	description: 'Navigation item which links to an Loading limits settings page',
});

const financialYearItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.financial-year-item',

	defaultMessage: 'Financial year',
	description: 'Navigation item which links to an Financial year settings page',
});

const archiveItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.archive-item',

	defaultMessage: 'Archive',
	description: 'Navigation item which links to an Archived plans settings page',
});

const trashedPlansItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.plans-section.trashed-plans-item',

	defaultMessage: 'Trash',
	description: 'Navigation item which links to an Advanced Roadmaps trashed plans settings page',
});

const PlansSection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'permissions' }}
					isSelected={currentRoute.name === 'arj-settings-permissions'}
					href="/jira/plans/settings/permissions"
				>
					{formatMessage(permissionsItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'hierarchyConfiguration' }}
					isSelected={currentRoute.name === 'global-settings-issues-hierarchy-view'}
					href="/jira/settings/issues/issue-hierarchy"
				>
					{formatMessage(hierarchyItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'dependencies' }}
					isSelected={currentRoute.name === 'arj-settings-dependencies'}
					href="/jira/plans/settings/dependencies"
				>
					{formatMessage(dependenciesItem)}
				</LinkMenuItem>
				{fg('jira_plans_loading_limits_configurability_stsg') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'loadingLimits' }}
						isSelected={currentRoute.name === 'arj-settings-loading-limits'}
						href="/jira/plans/settings/loading-limits"
					>
						{formatMessage(loadingLimitsItem)}
					</LinkMenuItem>
				)}
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'trash' }}
					isSelected={currentRoute.name === 'trash-plans-directory'}
					href="/jira/plans/settings/trash"
				>
					{formatMessage(trashedPlansItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'archive' }}
					isSelected={currentRoute.name === 'archived-plans-directory'}
					href="/jira/plans/settings/archive"
				>
					{formatMessage(archiveItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'financialYear' }}
					isSelected={currentRoute.name === 'arj-settings-financial-year'}
					href="/jira/plans/settings/financial-year"
				>
					{formatMessage(financialYearItem)}
				</LinkMenuItem>
			</MenuList>
		</MenuSection>
	);
};

const ExportedComponent: React.MemoExoticComponent<() => React.JSX.Element> = memo(PlansSection);
export default ExportedComponent;
